// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { OffsetModeViewEnum } from '@shared/utils/Trading/OffsetModeViewEnum';
import { ProductType } from '@shared/utils/Instruments/ProductType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { OrderTif } from '@shared/utils/Trading/OrderTifEnum';
import { SLTPTrigger } from '@shared/utils/SlTpTrigger';
import { AlertAfterExecute, AlertCondition, AlertImportance, AlertNotification, AlertType } from '@shared/utils/Alert/AlertConstants';
import { InsDefSettingsStorage } from '../cache/InstrumentDefaults';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { DisplayMode, getDisaplyModeHtmlAttribute } from '@shared/utils/Enums/DisplayMode';
import { ThemePreference } from '@shared/utils/ThemesEnum';
export class _BaseGeneralSettings {
    constructor() {
        // Need remove in the future begins block
        this.settingsChanged = new CustomEvent();
        this.displayModeChanged = new CustomEvent();
        this._insDefSettingsStorage = null;
        this.excludeSerialize = ['excludeSerialize', 'settingsChanged', 'settingsNeedSave', '_insDefSettingsStorage'];
        // GENERAL SETTINGS
        this.language = 'en';
        this.currentTheme = ThemePreference.Dark;
        this._displayMode = DisplayMode.Regular;
        this.onboardInFirstTimeShowed = false;
        this.disableInactivityPeriod = false;
        this.logSnapshotHasBeenSent = false;
        this._isCashModePreferred = false;
        this._isAllowOTHTrading = false;
        // TRADING DEFAULT SETTINGS
        this.tradingAccountID = null;
        this.tradingSymbolID = null;
        this._isSlTpInOffset = false;
        this._offsetType = OffsetModeViewEnum.Ticks;
        /**
         * Only for save/load. Use method {@link _BaseGeneralSettings.isUseStopLimitInsteadStop}
         *@type {boolean}
         */
        this.useStopLimitInsteadStop = false;
        this.limitOffsetTicks = 1000;
        this._orderType = OrderType.Market;
        this._productType = ProductType.General;
        this._orderTif = OrderTif.IOC;
        this.limitAndStopLimitValidity = OrderTif.GTC;
        this.stopValidity = OrderTif.GTC;
        this.typesManagerSettings = null;
        this.slTpTriggers = new SLTPTrigger(10);
        // CONFIRMATION SETTINGS
        this._confirmOrderSending = true;
        this._confirmOrderCancel = true;
        this._confirmOrderExecuting = true;
        this._confirmOrderModification = true;
        this._confirmOrderPositionsModification = true;
        this._confirmPositionClosing = true;
        this._confirmReversePosition = true;
        this._confirmPositionModification = true;
        this._confirmSubscriptions = true; //  Регулирует отображение скринов при подписке (скрин Subscription)/отказе от подписки (скрин Unsubscribe) на продукт. #104359
        this._confirmAccountFollowing = true; //  #123699
        this._positionSizeCalculatorInfoWindow = true;
        this._openDialTicketsOnWorkspace = false;
        this._showMarginDealTicketOnWorkspace = true;
        this._showDialTicketsInApplicationTray = true;
        this._showInformationMessageAboutSubscribeUnsubscribe = true;
        this._displayQuantityInLots = true;
        // ALERT SETTINGS
        this.confirmPlaceAlert = false;
        this.confirmCancelAlert = true;
        this._alertNotification = AlertNotification.SHOW_POP_UP + AlertNotification.SOUND;
        this._alertType = AlertType.LAST;
        this._alertCondition = AlertCondition.MORE;
        this._alertImportance = AlertImportance.MEDIUM;
        this._alertAfterExecute = AlertAfterExecute.REMOVE;
        // WARNING SETTINGS
        this.warnIfWrongOrder = true;
        this.warnIfIdenticalSubsequentOrderIsPlaced = false;
        this.warnIfIdenticalOrderTime = 3;
        this.warnIfQtyToCloseMoreThanMaxLot = false;
        this.overnightMarginNotificationMessage = true;
        this.warnWhenEnteringIntoLeverage = true;
        this.warnIfOrderQtyExceed = false;
        this.warnIfOrderQtyExceedValue = 100;
        this.warnIfOrderCapitalExceed = false;
        this.warnIfOrderCapitalExceedValue = 2000000;
        this.warnIfTodayGrossLess = false;
        this.warnIfTodayGrossLessValue = 0;
        this.warnIfAvailibleFundsLess = false;
        this.warnIfAvailibleFundsLesssValue = 1;
        this.warnIfTodayVolumeExeed = false;
        this.warnIfTodayVolumeExeedValue = 1;
        this.showInstrumentHalt = false;
        this.isShowOffMarketWarnings = true;
        // VIEW SETTINGS
        this.roundedAverageOpenPrice = true;
        this.reverseButtonsOrder = false;
        this.tooltips = true;
        this._abbreviateVolumes = true;
        // SOUND SETTINGS
        this.enableSounds = true;
        this.disableStartEndSound = true;
        this.enableWelcomeSound = false;
        this.enableFinishSound = false;
        //HIDDEN SETTINGS
        this._isSideBarCollapsed = false;
    }
    get insDefSettingsStorage() {
        if (this._insDefSettingsStorage === null) {
            this._insDefSettingsStorage = new InsDefSettingsStorage();
        }
        return this._insDefSettingsStorage;
    }
    set insDefSettingsStorage(storage) {
        this._insDefSettingsStorage = storage;
    }
    settingsChangedRaise() {
        this.settingsChanged.Raise();
    }
    // ends block
    reset() { }
    restore(newSettings) {
        for (const key in newSettings) {
            if (!this.excludeSerialize.includes(key)) {
                if (newSettings.hasOwnProperty(key)) {
                    this[key] = newSettings[key];
                }
            }
        }
    }
    get isSlTpInOffset() {
        return this._isSlTpInOffset;
    }
    set isSlTpInOffset(value) {
        this._isSlTpInOffset = value;
        this.onSettingsChanged();
    }
    get offsetType() {
        return this._offsetType;
    }
    set offsetType(value) {
        this._offsetType = value;
        this.onSettingsChanged();
    }
    get orderType() {
        return this._orderType;
    }
    set orderType(value) {
        this._orderType = value;
        this.onSettingsChanged();
    }
    get productType() {
        return this._productType;
    }
    set productType(value) {
        this._productType = value;
        this.onSettingsChanged();
    }
    get orderTif() {
        return this._orderTif;
    }
    set orderTif(value) {
        this._orderTif = value;
        this.onSettingsChanged();
    }
    get isCashModePreferred() {
        return this._isCashModePreferred;
    }
    set isCashModePreferred(value) {
        if (this._isCashModePreferred !== value) {
            this._isCashModePreferred = value;
            // this.onSettingsChanged(); /* #124573 */
        }
    }
    get isAllowOTHTrading() {
        return this._isAllowOTHTrading;
    }
    set isAllowOTHTrading(value) {
        if (this._isAllowOTHTrading !== value) {
            this._isAllowOTHTrading = value;
            this.onSettingsChanged();
        }
    }
    get confirmOrderSending() {
        return this._confirmOrderSending;
    }
    set confirmOrderSending(value) {
        this._confirmOrderSending = value;
        this.onSettingsChanged();
    }
    get confirmOrderCancel() {
        return this._confirmOrderCancel;
    }
    set confirmOrderCancel(value) {
        this._confirmOrderCancel = value;
        this.onSettingsChanged();
    }
    get confirmOrderExecuting() {
        return this._confirmOrderExecuting;
    }
    set confirmOrderExecuting(value) {
        this._confirmOrderExecuting = value;
        this.onSettingsChanged();
    }
    get confirmOrderModification() {
        return this._confirmOrderModification;
    }
    set confirmOrderModification(value) {
        this._confirmOrderModification = value;
        this.onSettingsChanged();
    }
    get confirmOrderPositionsModification() {
        return this._confirmOrderPositionsModification;
    }
    set confirmOrderPositionsModification(value) {
        this._confirmOrderPositionsModification = value;
        this.onSettingsChanged();
    }
    get confirmPositionClosing() {
        return this._confirmPositionClosing;
    }
    set confirmPositionClosing(value) {
        this._confirmPositionClosing = value;
        this.onSettingsChanged();
    }
    get confirmReversePosition() {
        return this._confirmReversePosition;
    }
    set confirmReversePosition(value) {
        this._confirmReversePosition = value;
        this.onSettingsChanged();
    }
    get confirmPositionModification() {
        return this._confirmPositionModification;
    }
    set confirmPositionModification(value) {
        this._confirmPositionModification = value;
        this.onSettingsChanged();
    }
    get confirmSubscriptions() {
        return this._confirmSubscriptions;
    }
    set confirmSubscriptions(value) {
        this._confirmSubscriptions = value;
        this.onSettingsChanged();
    }
    get confirmAccountFollowing() {
        return this._confirmAccountFollowing;
    }
    set confirmAccountFollowing(value) {
        this._confirmAccountFollowing = value;
        this.onSettingsChanged();
    }
    get positionSizeCalculatorInfoWindow() {
        return this._positionSizeCalculatorInfoWindow;
    }
    set positionSizeCalculatorInfoWindow(value) {
        this._positionSizeCalculatorInfoWindow = value;
        this.onSettingsChanged();
    }
    get openDialTicketsOnWorkspace() {
        return this._openDialTicketsOnWorkspace;
    }
    set openDialTicketsOnWorkspace(value) {
        this._openDialTicketsOnWorkspace = value;
        this.onSettingsChanged();
    }
    get showMarginDealTicketOnWorkspace() {
        return this._showMarginDealTicketOnWorkspace;
    }
    set showMarginDealTicketOnWorkspace(value) {
        this._showMarginDealTicketOnWorkspace = value;
        this.onSettingsChanged();
    }
    get showDialTicketsInApplicationTray() {
        return this._showDialTicketsInApplicationTray;
    }
    set showDialTicketsInApplicationTray(value) {
        this._showDialTicketsInApplicationTray = value;
        this.onSettingsChanged();
    }
    get showInformationMessageAboutSubscribeUnsubscribe() {
        return this._showInformationMessageAboutSubscribeUnsubscribe;
    }
    set showInformationMessageAboutSubscribeUnsubscribe(value) {
        this._showInformationMessageAboutSubscribeUnsubscribe = value;
        this.onSettingsChanged();
    }
    get isQuantityInLots() {
        return this._displayQuantityInLots;
    }
    set isQuantityInLots(value) {
        this._displayQuantityInLots = value;
        this.onSettingsChanged();
    }
    get displayMode() {
        return this._displayMode;
    }
    set displayMode(value) {
        this._displayMode = value;
        this.onSettingsChanged();
        this.displayModeChanged.Raise();
        setHtmlAttribute('data-display-mode', getDisaplyModeHtmlAttribute(value));
    }
    get alertNotification() {
        return this._alertNotification;
    }
    set alertNotification(value) {
        this._alertNotification = value;
        this.onSettingsChanged();
    }
    get alertType() {
        return this._alertType;
    }
    set alertType(value) {
        this._alertType = value;
        this.onSettingsChanged();
    }
    get alertCondition() {
        return this._alertCondition;
    }
    set alertCondition(value) {
        this._alertCondition = value;
        this.onSettingsChanged();
    }
    get alertImportance() {
        return this._alertImportance;
    }
    set alertImportance(value) {
        this._alertImportance = value;
        this.onSettingsChanged();
    }
    get alertAfterExecute() {
        return this._alertAfterExecute;
    }
    set alertAfterExecute(value) {
        this._alertAfterExecute = value;
        this.onSettingsChanged();
    }
    get abbreviateVolumes() {
        return this._abbreviateVolumes;
    }
    set abbreviateVolumes(value) {
        this._abbreviateVolumes = value;
        this.onSettingsChanged();
    }
    get isSideBarCollapsed() {
        return this._isSideBarCollapsed;
    }
    set isSideBarCollapsed(value) {
        this._isSideBarCollapsed = value;
        this.onSettingsChanged();
    }
    isUseStopLimitInsteadStop() {
        return this.useStopLimitInsteadStop && this.offsetType !== OffsetModeViewEnum.Percent;
    }
    onSettingsChanged() {
        this.settingsChangedRaise();
    }
}
